/* Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BaseViewer, ScrollMode } from './base_viewer';
import { getVisibleElements, scrollIntoView, isMobile } from './ui_utils';
import { shadow } from 'pdfjs-lib';

class PDFViewer extends BaseViewer {
  get _setDocumentViewerElement() {
    return shadow(this, '_setDocumentViewerElement', this.viewer);
  }

  _scrollIntoView({ pageDiv, pageSpot = null, }) {
    if (!pageSpot && !this.isInPresentationMode) {
      const left = pageDiv.offsetLeft + pageDiv.clientLeft;
      const right = left + pageDiv.clientWidth;
      const { scrollLeft, clientWidth, } = this.container;
      if (this._scrollMode === ScrollMode.HORIZONTAL ||
          left < scrollLeft || right > scrollLeft + clientWidth) {
        pageSpot = { left: 0, top: 0, };
      }
    }
    scrollIntoView(pageDiv, pageSpot);
  }

  _getVisiblePages() {
    if (!this.isInPresentationMode && !this.book) {
      return getVisibleElements(this.container, this._pages, true,
                                this._scrollMode === ScrollMode.HORIZONTAL);
    }
    // The algorithm in getVisibleElements doesn't work in all browsers and
    // configurations when presentation mode is active.
    let currentPage = this._pages[this._currentPageNumber - 1];
    let lastPage = currentPage;
    let visible = [{ id: currentPage.id, view: currentPage, }];
    return { first: currentPage, last: lastPage, views: visible, };
  }

  forceRenderingCache() {
    let range = this.book.turn('is') ? this.book.turn('range') : [0, 0];
    let start = range[0], end = range[1];

    if (start > 2) {
      start -= 2;
    }

    for (let i = start; i < end; i++) {
      let pageView = this._pages[i];
      this._ensurePdfPageLoaded(pageView).then(() => {
        this.renderingQueue.renderView(pageView);
      });
    }
  }

  resize() {
    console.log('resize');
    let left = 0;
    let top = 32;

    if (isMobile()) {
      top = 0;
      this.vpadding = 0;
      this.hpadding = 0;
    } else {
      left = this.hpadding;
    }
    let curPage = this.currentPageNumber;
    let viewport = curPage < 0 || curPage > this._pages.length -1 ? this._pages[0].viewport : this._pages[curPage].viewport;
    let bookWidth = viewport.width;
    let bookHeight = viewport.height;
    let bookRatio = bookWidth / bookHeight;

    let clientWidth = $(window).width() - this.hpadding * 2;
    let clientHeight = $(window).height() - this.vpadding;
    let ratio = clientWidth / bookWidth;

    let display = 'double';
    let clientRatio = 0;

    if (this.book.turn('is')) {
      display = this.book.turn('display');
    } else {
      let isVertical = clientHeight > clientWidth;
      let isDouble = ratio >= 2 || !isVertical;
      display = isDouble ? 'double' : 'single';
    }

    clientRatio = display === 'double' ? (clientWidth / 2) / clientHeight : clientWidth / clientHeight;

    if (this.dimension === 'single') { // Force to use single page
      display = 'single';
      clientRatio = clientWidth / clientHeight;
    }

    if (clientRatio > bookRatio) {
      bookHeight = Math.ceil(clientHeight);
      bookWidth = Math.ceil(clientHeight * bookRatio);
      if (this.dimension === 'single') {
        bookWidth *= 2;
      }

      left = (clientWidth - (display === 'double' ? bookWidth * 2 : bookWidth)) / 2;
    } else {
      bookWidth = Math.ceil(display === 'double' ? clientWidth / 2 : clientWidth);
      bookHeight = Math.ceil(bookWidth / bookRatio);

      top += (clientHeight - bookHeight) / 2;
      left = this.hpadding;
    }

    let delta = Math.ceil(bookWidth - viewport.width) + 1;
    //console.log('PDF Width: ' + viewport.width + ', Book Width: ' + bookWidth + ', delta:' + delta);
    if (this.book.turn('is')) {
      this.book.turn('size', (bookWidth - delta) * (display === 'double' ? 2 : 1), bookHeight - delta);
    }

    if (isMobile() && left == 0)
      left = 2;

    $(this.container)
      .css('width', bookWidth * 2 + this.hpadding)
      .css('height', bookHeight).css('left', left).css('top', top);

    //console.log({ width: bookWidth, height: bookHeight, });

    let that = this;
    if (!this.book.turn('is')) {
      this.book.turn({
        width: bookWidth,
        height: bookHeight,
        acceleration: true,
        autoCenter: true,
        pages: that.pagesCount,
        elevation: 10,
        duration: 800,
        gradients: true,
        display: display,
        when: {
          turning: function(e, page, view) {
            that.currentPageNumber = page;
            that.forceRendering();
            if(page != view[0]){
              that.currentPageNumber = view[0];
              that.forceRendering();
            }
          },
          turned: function(e, page, view){
            that.forceRenderingCache();
          },
          zooming: function(e, newZoom, currentZoom){
            //console.log(newZoom);
            //console.log(currentZoom);
            that.eventBus.dispatch('scalechanged', {
              source: self,
              value: newZoom,
            });
          }
        }
      });

      $(this.container).css('visibility', 'visible');
    }
  }

  update() {
    let visible = this._getVisiblePages();
    let visiblePages = visible.views, numVisiblePages = visiblePages.length;
    if (numVisiblePages === 0) {
      return;
    }
    this._resizeBuffer(numVisiblePages, visiblePages);

    this.renderingQueue.renderHighestPriority(visible);

    let currentId = this._currentPageNumber;
    let stillFullyVisible = false;

    for (let i = 0; i < numVisiblePages; ++i) {
      let page = visiblePages[i];

      if (page.percent < 100) {
        break;
      }
      if (page.id === currentId) {
        stillFullyVisible = true;
        break;
      }
    }

    if (!stillFullyVisible) {
      currentId = visiblePages[0].id;
    }
    if (!this.isInPresentationMode) {
      this._setCurrentPageNumber(currentId);
    }

    this._updateLocation(visible.first);
    this.eventBus.dispatch('updateviewarea', {
      source: this,
      location: this._location,
    });
  }

  get _isScrollModeHorizontal() {
    // Used to ensure that pre-rendering of the next/previous page works
    // correctly, since Scroll/Spread modes are ignored in Presentation Mode.
    return (this.isInPresentationMode ?
            false : this._scrollMode === ScrollMode.HORIZONTAL);
  }
}

export {
  PDFViewer,
};
